import ArrowRight from "@mui/icons-material/KeyboardArrowRight";
import {
  Box,
  Button,
  Collapse,
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  formatDateStrings,
  shortFormatDateStrings,
} from "../../../../app/common";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { ReactComponent as ClockIcon } from "../../../../common/icons/clock.svg";
import {
  Assignment,
  EmploymentAndAttachmentRow,
} from "../../../talents/talentAPI";
import { EmploymentRowType } from "../types";
import { Link, useParams } from "react-router-dom";
import { setContinousEmploymentId } from "../../../employment/employmentSlice";
import InfoTooltip from "../../../../common/components/functional/InfoTooltip/InfoTooltip";
import { FlexVerticalAligned } from "../../../../common/components/styled/styled";
import { useState } from "react";
import BundleHeader from "./BundleHeader/BundleHeader";
import isEmpty from "../../../../common/helpers/isEmpty";

interface Props {
  disabled: boolean;
  index: number;
  type: EmploymentRowType;
  employment: EmploymentAndAttachmentRow;
  tesName: string;
  employerName: string;
  bundleIndex?: number;
  isShift: boolean;
  continuousEmploymentId: string | undefined;
}

const TooltipCell = ({ tooltipText }: { tooltipText: string }) => {
  return (
    <Tooltip title={tooltipText}>
      <Box
        sx={{
          textOverflow: "ellipsis",
          overflow: "hidden",
          whiteSpace: "nowrap",
        }}
      >
        {tooltipText}
      </Box>
    </Tooltip>
  );
};

const FixedTableCell = ({
  width,
  children,
  sx,
}: {
  width: string;
  children: React.ReactNode;
  sx?: SxProps;
}) => {
  return (
    <TableCell
      sx={{
        ...sx,
        maxWidth: width,
        minWidth: width,
        width: width,
      }}
    >
      {children}
    </TableCell>
  );
};

const EmploymentRow = ({
  disabled,
  index,
  type,
  employment,
  tesName,
  employerName,
  bundleIndex,
  isShift,
  continuousEmploymentId,
}: Props) => {
  const { t } = useTranslation();
  const { talentId } = useParams();
  const dispatch = useAppDispatch();

  const translatedWorkTitles = useAppSelector(
    (state) => state.localized.workTitle,
  );

  // Check starting color and if it's even or odd row
  const even = (index + (bundleIndex || 0)) % 2 === 0;
  const isBundle =
    employment.assignments.length > 1 ||
    !isEmpty(employment.targetedAssignments); // TODO: is there another way to check if it's a bundle?
  // figure out current employment start year so that it can be hidden from assignment time stamps
  const emplStartYear = new Date(employment.startDate).getFullYear();

  const [targetedAssignmentsBundleOpen, setTargetedAssignmentsBundleOpen] =
    useState(false);

  const AssignmentRow = ({
    assignment,
    isTargetedAssignment = false,
  }: {
    assignment: Assignment;
    isTargetedAssignment?: boolean;
  }) => {
    return (
      <Tooltip
        arrow
        title={
          isTargetedAssignment && assignment.cancellation
            ? t("talentProfile:assignmentIsCancelled")
            : undefined
        }
      >
        <TableRow
          sx={{
            borderLeft: "1px solid #E5E5E5",
            height: "50px",
            "& > td": {
              height: "auto !important",
              padding: "10px 8px",
              borderBottom: "none",
            },
            opacity: isTargetedAssignment && assignment.cancellation ? 0.6 : 1,
          }}
        >
          <FixedTableCell width="100px">
            <FlexVerticalAligned>
              {!isShift && (
                <>
                  {!isTargetedAssignment
                    ? shortFormatDateStrings(
                        assignment.startDate,
                        emplStartYear,
                        assignment.endDate,
                      )
                    : formatDateStrings(
                        assignment.startDate,
                        assignment.endDate,
                        true,
                      )}
                </>
              )}
            </FlexVerticalAligned>
          </FixedTableCell>
          <FixedTableCell width="160px">
            <TooltipCell tooltipText={assignment.customerOrgName} />
          </FixedTableCell>
          <FixedTableCell width="160px">
            <TooltipCell tooltipText={assignment.workLocationName} />
          </FixedTableCell>
          <FixedTableCell width="160px">
            <TooltipCell
              tooltipText={tesName.replace("työehtosopimus", "TES")}
            />
          </FixedTableCell>
          <FixedTableCell width="130px">
            <TooltipCell
              tooltipText={translatedWorkTitles[assignment.workTitleId]}
            />
          </FixedTableCell>
          <FixedTableCell
            width="160px"
            sx={{
              padding: "10px 8px !important",
            }}
          >
            <Button
              component={Link}
              to={`/home/talents/${talentId}/assignment/${assignment.assignmentId}`}
              endIcon={
                <ArrowRight
                  sx={{
                    height: "16px",
                    width: "16px",
                  }}
                />
              }
            >
              <Typography variant="header" fontWeight="500" fontSize="12px">
                {t("talentProfile:assignmentAdditionalInfo")}
              </Typography>
            </Button>
          </FixedTableCell>
          <FixedTableCell width="95px">
            {assignment.invoicingPriceOverride && (
              <FlexVerticalAligned>
                <Typography variant="body2" mr="4px">
                  {`${assignment.invoicingPriceOverride} ${t("common:hourlySalaryUnit")}`}
                </Typography>
                <InfoTooltip
                  title={t("talentProfile:employmentHourlySalaryInfo")}
                />
              </FlexVerticalAligned>
            )}
          </FixedTableCell>
        </TableRow>
      </Tooltip>
    );
  };

  return (
    <TableRow
      sx={{
        background: even ? "#FAFAFB" : "#F3F3F7",
        opacity:
          type === EmploymentRowType.Upcoming ||
          type === EmploymentRowType.Cancelled
            ? 0.6
            : 1,
        padding: "0px 8px !important",
        "& > td": {
          borderBottom: "none",
          padding: isBundle ? "16px 0px" : "0px 0px",
          verticalAlign: isBundle ? "top" : "middle",
        },
      }}
    >
      <FixedTableCell width="60px">
        {isShift && <ClockIcon style={{ margin: "6px 0px 0px 26px" }} />}
      </FixedTableCell>
      <FixedTableCell width="120px">
        {formatDateStrings(employment.startDate, employment?.endDate || null)}
      </FixedTableCell>
      <FixedTableCell width="155px">
        <TooltipCell tooltipText={employerName} />
      </FixedTableCell>
      <FixedTableCell width="165px" sx={{ padding: "10px 8px !important" }}>
        <Button
          disabled={disabled}
          component={Link}
          to={`/home/talents/${talentId}/employment/${employment.employmentId}`}
          onClick={() => {
            // this should not be needed - if the page is later reloaded, this is not done and the continuousEmploymentId set here is forgotten
            // contEmpId is received when employmentSlice when the employment is anyway fetched
            dispatch(setContinousEmploymentId(continuousEmploymentId || ""));
          }}
          endIcon={
            <ArrowRight
              sx={{
                height: "16px",
                width: "16px",
                color: (theme) =>
                  disabled ? "text.disabled" : theme.palette.primary.main,
              }}
            />
          }
        >
          <Typography variant="header" fontWeight="500" fontSize="12px">
            {t("talentProfile:employmentAdditionalInfo")}
          </Typography>
        </Button>
      </FixedTableCell>
      <TableCell sx={{ padding: "0 !important", borderBottom: "none" }}>
        <Table>
          <TableBody>
            {employment.assignments.map((assignment) => (
              <AssignmentRow
                key={assignment.assignmentId}
                assignment={assignment}
              />
            ))}
          </TableBody>
        </Table>
        {!isEmpty(employment.targetedAssignments) && (
          <>
            <BundleHeader
              open={targetedAssignmentsBundleOpen}
              setOpen={setTargetedAssignmentsBundleOpen}
              employmentBundle={undefined}
              title={t(
                "talentProfile:targetedShifts.employmentTargetedAssignments",
              )}
              inner={true}
              bundleId={undefined}
            />
            <Collapse in={targetedAssignmentsBundleOpen}>
              <Table>
                <TableBody>
                  {employment.targetedAssignments.map(
                    (assignment) => (
                      <AssignmentRow
                        key={assignment.assignmentId}
                        assignment={assignment}
                        isTargetedAssignment
                      />
                    ),
                  )}
                </TableBody>
              </Table>
            </Collapse>
          </>
        )}
      </TableCell>
    </TableRow>
  );
};

export default EmploymentRow;
